import LoadmoreInfo from '@components/PropertySearchResults/LoadmoreInfo'
import OverviewHeading from '@components/PropertySearchResults/OverviewHeading'
import SearchResults from '@components/PropertySearchResults/SearchResults'
import SearchResultsBar from '@components/PropertySearchResults/SearchResultsBar'
import SearchResultCount from '@components/PropertySearchResults/SearchResultsCount'
import SuburbApiDataFromName from '@components/PropertySearchResults/SuburbProfile/helpers'
import { LISTING_TYPE, PROPERTY_RESULTS_PATH } from '@constants/property'
import TwinEqualCTA from '@global/Blocks/TwinEqualCTA'
import Breadcrumbs from '@global/Breadcrumbs'
import SuburbSEOLinks from '@global/SuburbSEOLinks'
import {
  getMapProperties,
  getProperties,
} from '@lib/api-services/propertiesService'
import { JWT_COOKIE, getCookie } from '@lib/cookies'
import { replaceAll, titleCase } from '@lib/helpers'
import { getPropertyResultsMetaData } from '@lib/helpers/search'
import {
  channelCriteriaMap,
  channelSlugToString,
  domain,
  itemShowPerPage,
  listSearchPageType,
  listingTypeSlugs,
  propertyTypeMap,
  queryApiParams,
  stateCodeStrings,
  suburbCategoriesExtraCriteria,
  suburbCategoriesSlugs,
} from '@lib/variables'
import Routes from '@routes'
import { Container, Section } from '@styles/global.styled'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import { useEffect, useState } from 'react'
import PropertyAlertLanding from '../layouts/PropertyAlertLanding'
import { COLORS } from '@styles/colors'

function generateBreadcrumbs(path) {
  if (!path) return []
  const segments = path.split('/').filter(Boolean)
  const breadcrumbs = segments.map((segment, index) => {
    const url = '/' + segments.slice(0, index + 1).join('/')
    return {
      label: titleCase(
        segment
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      ),
      url: url,
    }
  })

  return breadcrumbs
}

const SuburbProperties = ({
  propertiesData,
  searchParams,
  slug,
  suburbData,
  suburbSlug,
  suburbList,
  SEO,
  searchView,
  mapResults,
}) => {
  const [properties, setProperties] = useState({
    ...propertiesData,
  })
  const [queryParams, setQueryParams] = useState({
    ...searchParams,
  })

  useEffect(() => {
    setProperties(propertiesData)
  }, [propertiesData])

  useEffect(() => {
    setQueryParams(searchParams)
  }, [searchParams])

  const breadcrumbs = generateBreadcrumbs(suburbData?.meta?.path)
  const metaData = getPropertyResultsMetaData(queryParams, properties.count)

  const changeView = async (view) => {
    const { query } = Routes.Router.router.state

    delete query.has_auction
    delete query.is_inspection
    delete query.view
    delete query.offmarket_only

    switch (view) {
      case 'auctions':
        query.has_auction = true
        break

      case 'inspections':
        query.is_inspection = true
        break

      case 'exclusivepreview':
        query.offmarket_only = 1
        break

      case 'map':
        query.view = view
        break
    }

    const route = Routes.findAndGetUrls('suburbProperties', {
      ...query,
    })

    await Routes.Router.pushRoute(route.urls.as)
    setQueryParams((prev) => ({ ...prev, view }))
  }

  const changeOrder = async (order) => {
    const { query } = Routes.Router.router.state
    if (query.order_by && query.order_by === order) return false
    query.order_by = order

    const route = Routes.findAndGetUrls('suburbProperties', {
      ...query,
    })

    await Routes.Router.pushRoute(route.urls.as)
  }

  const onClearSearch = () => {
    const { view, listing_type } = searchParams
    const viewPaths = {
      inspections: '/for-sale/open-for-inspections/',
      auctions: '/for-sale/auctions/',
    }

    let url = PROPERTY_RESULTS_PATH[listing_type]
    if (viewPaths[view]) {
      url = viewPaths[view]
    }

    Routes.Router.pushRoute(url)
  }

  const numberOfSuburbOffices = suburbList?.filter(it => it?.offices?.length > 0)?.length

  return (
    <>
      {/* If the page has no results, we don't want Google to index it */}
      <NextSeo
        {...SEO}
      // noindex:
      //   !propertiesData ||
      //   !propertiesData.results ||
      //   propertiesData.results.length == 0 ||
      //   PropertyAlertLanding.results == 0
      />
      {/* Manually doing no-index as we want to keep follow */}
      {!properties ||
        !properties.results ||
        properties.results.length == 0 ||
        PropertyAlertLanding.results == 0 ? (
        <Head>
          <meta
            name='robots'
            content='noindex,follow,noarchive'
            className='next-head'
          />
          <meta
            name='googlebot'
            content='noindex,follow,noarchive'
            className='next-head'
          />
        </Head>
      ) : null}

      {breadcrumbs?.length > 0 && <Container>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Suburb Profiles', url: breadcrumbs[0].url },
            breadcrumbs[1],
            {
              label:
                queryParams.listing_type === LISTING_TYPE.SALE
                  ? 'For Sale'
                  : 'For Lease',
              url: PROPERTY_RESULTS_PATH[queryParams.listing_type],
            },
            { label: suburbData.suburb_name, url: '/' },
          ]}
        />
      </Container>}
      <Section paddingSmall>
        <Container>
          <OverviewHeading
            metaData={metaData}
            orderBy={queryParams.order_by}
            isSuburbProperties
            queryParams={queryParams}
            offices={suburbList?.[0]?.offices}
            onOrderChange={changeOrder}
            pageType={listSearchPageType.SEARCH_TYPE_PROPERTY}
          />
          <SearchResultsBar
            onViewChange={changeView}
            view={queryParams.view}
            pageType={listSearchPageType.SEARCH_TYPE_PROPERTY}
            disabled={metaData.total === 0}
          />

          <SearchResultCount searchView={searchView} metaData={metaData} />

          <SearchResults
            items={properties.results}
            totalItems={metaData.total}
            mapResults={mapResults}
            searchView={searchView}
            queryParams={queryParams}
            onClearSearch={onClearSearch}
          />
          {!!properties?.results?.length &&
            metaData.end < metaData.total &&
            searchView !== 'map' ? (
            <LoadmoreInfo
              queryParams={{ ...searchParams, page: queryParams?.page || 1 }}
              metaData={metaData}
              searchPageType={listSearchPageType.SEARCH_TYPE_PROPERTY}
              onSuccess={(newItems, newPage) => {
                const newReresults = properties.results.concat(newItems)
                setQueryParams((prev) => ({ ...prev, page: newPage }))
                setProperties((prev) => ({
                  ...prev,
                  results: [...newReresults],
                }))
              }}
            />
          ) : null}
        </Container>
      </Section>

      <Section>
        {(numberOfSuburbOffices > 0 || (numberOfSuburbOffices === 0 && Object.keys(suburbData?.property_stats || {}).length > 0)) && suburbList.map((it) => (
          <TwinEqualCTA
            key={it.suburb}
            title={`Looking to purchase or rent a property in ${titleCase(
              it.suburb
            )}?`}
            items={it.offices.map((it) => ({
              ...it,
              description: it.address,
            }))}
          />
        ))}
      </Section>

      {!!(suburbData && suburbSlug) ? (
        <Section noPadding>
          <SuburbSEOLinks
            noPaddingTop={suburbList?.length > 0}
            propertyStats={suburbData.property_stats}
            suburb={suburbData.title}
            state={suburbData.state}
            slug={suburbSlug}
          />
        </Section>
      ) : null}
    </>
  )
}

SuburbProperties.getInitialProps = async ({ query, ...ctx }) => {
  const slug = ctx.asPath
  let searchParams = {
    per_page: itemShowPerPage.ListingSearchPage,
  }
  let mapResults = null
  const token = getCookie(JWT_COOKIE, ctx)

  Object.keys(query).map((key) => {
    // Clean up 1-1 mapping of fields from query params
    if (queryApiParams[key]) {
      searchParams[queryApiParams[key]] = query[key]
    } else if (key === 'channel') {
      searchParams = {
        ...searchParams,
        ...channelCriteriaMap[query[key]],
      }
    } else if (key === 'categories') {
      searchParams.categories = propertyTypeMap[query[key]] || query[key]
    } else {
      searchParams[key] = query[key]
    }
  })

  if (!searchParams.order_by) {
    searchParams.order_by = '-created'
  }

  if (
    searchParams.listing_type &&
    Object.keys(listingTypeSlugs).indexOf(searchParams.listing_type) !== -1
  ) {
    searchParams.listing_type = listingTypeSlugs[searchParams.listing_type]
  }

  // Rewriting category names
  if (
    searchParams.categories &&
    Object.keys(suburbCategoriesSlugs).indexOf(searchParams.categories) !== -1
  ) {
    searchParams.categories = suburbCategoriesSlugs[searchParams.categories]
  }

  // Some categories are actually "flags" for more complicated search params
  if (
    Object.keys(suburbCategoriesExtraCriteria).indexOf(
      searchParams.categories
    ) != -1
  ) {
    searchParams = {
      ...searchParams,
      ...suburbCategoriesExtraCriteria[searchParams.categories],
    }
    delete searchParams.categories
  }

  const suburbSlug = searchParams.address_suburb
  // Convert suburb to spaces and uppercase
  if (searchParams.address_suburb) {
    searchParams.address_suburb =
      replaceAll(searchParams.address_suburb, '-', ' ')
        .toUpperCase()
  }

  if (searchParams.view && searchParams.view === 'map') {
    searchParams.per_page = 4
    mapResults = await getMapProperties(searchParams, token || false)
  }

  // handle 5+ bedrooms
  if(searchParams.bedrooms == 5) {
    searchParams.bedrooms__gte = 5
    delete searchParams.bedrooms
  }

  const propertiesData = await getProperties({
    ...searchParams,
    per_page: itemShowPerPage.ListingSearchPage,
  })

  let suburbData
  let suburbName = searchParams.address_suburb || ''
  let state = query.state || ''
  // Clean up the category name for display
  let categoryName = query.categories || 'Real Estate'
  categoryName = replaceAll(categoryName, '-', ' ')
  categoryName = titleCase(categoryName)

  if (searchParams.bedrooms) {
    categoryName = `${searchParams.bedrooms} Bedroom Properties`
  }

  if (searchParams.address_suburb) {
    suburbData = await SuburbApiDataFromName(
      titleCase(replaceAll(searchParams.address_suburb, '-', ' '))
    )

    suburbName = suburbData
      ? suburbData.suburb_name
      : searchParams.address_suburb || ''

    state = suburbData ? suburbData.state : query.state || ''
  }

  const defaultCategoryTitle = 'Real Estate & Property'

  let seoTitle, seoDescription

  if (searchParams.bedrooms) {
    seoTitle = `Latest ${searchParams.bedrooms} Bedroom Properties ${channelSlugToString[query.channel]
      } in ${suburbName}, ${stateCodeStrings[state.toLowerCase()] || state}`
    seoDescription = `Browse all Barry Plant's available ${searchParams.bedrooms
      } bedroom properties ${channelSlugToString[
        query.channel
      ].toLowerCase()} in ${suburbName}, ${stateCodeStrings[state.toLowerCase()] || state
      }. View the latest listings and find your perfect everyday sanctuary with Barry Plant.
`
  } else {
    seoTitle = `Latest ${query.categories && categoryName ? categoryName : defaultCategoryTitle
      } ${channelSlugToString[query.channel]} in ${suburbName}, ${stateCodeStrings[state.toLowerCase()] || state
      }`

    seoDescription = `Browse all Barry Plant's available ${query.categories && categoryName
      ? categoryName
      : defaultCategoryTitle.toLowerCase()
      } ${channelSlugToString[query.channel].toLowerCase()} in ${suburbName}, ${stateCodeStrings[state.toLowerCase()] || state
      }. View the latest listings and find your perfect everyday sanctuary with Barry Plant.`
  }

  const SEO = {
    canonical: `${domain}${slug}`,
    title: seoTitle,
    description: seoDescription,
  }

  const suburbList = propertiesData.suburb_list || []

  return {
    SEO,
    propertiesData,
    searchParams,
    slug,
    suburbData,
    suburbSlug,
    mapResults,
    suburbList,
    searchView: searchParams.view,
  }
}

export default SuburbProperties
